/**
 * ApiKeyManager - Secure storage and retrieval of user API keys
 *
 * Keys are encrypted at rest in localStorage using XOR with a random 256-bit
 * key.  The encryption key is itself stored in localStorage under an
 * innocuous name and obfuscated so it is not trivially discoverable alongside
 * the ciphertext.
 *
 * Threat model: prevents casual exposure from a localStorage dump (e.g.
 * devtools, backup, screenshot).  A determined attacker with XSS can still
 * read both the ciphertext and the key from the same origin, but the bar is
 * significantly higher than plaintext base64.
 *
 * For production-grade protection, keys should be stored on the backend
 * (see P1: Adopt Backend API Key Auth in CLAUDE.md).
 */

export class ApiKeyManager {
  static STORAGE_KEY = 'user_api_keys';

  // ── Encryption key management ─────────────────────────────────────────────

  /** localStorage key for the obfuscated encryption key (innocuous name). */
  static _EK_STORAGE = '_app_cfg_v2';
  /** Fixed salt used to obfuscate the encryption key itself. */
  static _SALT = 'hkrds1996-akm-v2-salt';

  /**
   * Get or create the 256-bit XOR encryption key.
   * Cached in sessionStorage for fast repeated access; persisted in
   * localStorage (obfuscated) so it survives tab closure.
   * @returns {string} Hex-encoded 256-bit key (64 chars)
   */
  static _getEncryptionKey() {
    // Fast path: sessionStorage cache (avoids deobfuscation on every call)
    try {
      const cached = sessionStorage.getItem('_akm_ek');
      if (cached && cached.length === 64) return cached;
    } catch { /* sessionStorage unavailable in some contexts */ }

    // Check localStorage (obfuscated)
    try {
      const stored = localStorage.getItem(this._EK_STORAGE);
      if (stored) {
        const key = this._deobfuscateKey(stored);
        if (key && key.length === 64) {
          try { sessionStorage.setItem('_akm_ek', key); } catch { /* ok */ }
          return key;
        }
      }
    } catch { /* corrupt data — regenerate */ }

    // Generate new 256-bit key
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const key = Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    try {
      localStorage.setItem(this._EK_STORAGE, this._obfuscateKey(key));
      sessionStorage.setItem('_akm_ek', key);
    } catch { /* storage full — key will be regenerated next time */ }

    return key;
  }

  /** Obfuscate the encryption key for storage (reverse + XOR with salt + base64). */
  static _obfuscateKey(key) {
    const reversed = key.split('').reverse().join('');
    let result = '';
    for (let i = 0; i < reversed.length; i++) {
      result += String.fromCharCode(
        reversed.charCodeAt(i) ^ this._SALT.charCodeAt(i % this._SALT.length)
      );
    }
    return btoa(result);
  }

  /** Reverse the obfuscation. */
  static _deobfuscateKey(obfuscated) {
    const decoded = atob(obfuscated);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(
        decoded.charCodeAt(i) ^ this._SALT.charCodeAt(i % this._SALT.length)
      );
    }
    return result.split('').reverse().join('');
  }

  // ── Encrypt / Decrypt ─────────────────────────────────────────────────────

  /**
   * Encrypt plaintext with XOR + base64, prefixed with "v2:" to distinguish
   * from the legacy btoa format.
   * @param {string} text - Plaintext to encrypt
   * @returns {string} Encrypted string ("v2:<base64>")
   */
  static encrypt(text) {
    try {
      const key = this._getEncryptionKey();
      let xored = '';
      for (let i = 0; i < text.length; i++) {
        xored += String.fromCharCode(
          text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
      }
      return 'v2:' + btoa(xored);
    } catch (error) {
      console.error('[ApiKeyManager] Encryption error:', error);
      return text;
    }
  }

  /**
   * Decrypt a stored value. Handles both the new "v2:" format and the legacy
   * btoa format (auto-migrates legacy values on next write via getApiKey).
   * @param {string} encrypted - Encrypted text
   * @returns {string} Plaintext
   */
  static decrypt(encrypted) {
    try {
      // New format: "v2:<base64(xor)>"
      if (encrypted.startsWith('v2:')) {
        const key = this._getEncryptionKey();
        const decoded = atob(encrypted.slice(3));
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
          result += String.fromCharCode(
            decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length)
          );
        }
        return result;
      }

      // Legacy format: plain btoa
      return atob(encrypted);
    } catch (error) {
      console.error('[ApiKeyManager] Decryption error:', error);
      return encrypted;
    }
  }

  // ── Public API (unchanged signatures) ─────────────────────────────────────
  
  /**
   * Store API key securely
   * @param {string} provider - Provider name (e.g., 'brave', 'openai')
   * @param {string} apiKey - The API key to store
   */
  static setApiKey(provider, apiKey) {
    if (!provider || !apiKey) {
      throw new Error('Provider and API key are required');
    }
    
    const keys = this.getAllApiKeys();
    keys[provider] = this.encrypt(apiKey);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(keys));
  }

  /**
   * Retrieve API key (auto-migrates legacy btoa values to v2: format).
   * @param {string} provider - Provider name
   * @returns {string|null} Decrypted API key or null
   */
  static getApiKey(provider) {
    const keys = this.getAllApiKeys();
    if (!keys[provider]) return null;

    const plaintext = this.decrypt(keys[provider]);

    // Auto-migrate: if the stored value was legacy btoa, re-encrypt with v2:
    if (!keys[provider].startsWith('v2:')) {
      keys[provider] = this.encrypt(plaintext);
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(keys));
      } catch { /* non-fatal */ }
    }

    return plaintext;
  }

  /**
   * Get all stored API keys (encrypted)
   * @returns {Object} Map of provider -> encrypted key
   */
  static getAllApiKeys() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('[ApiKeyManager] Error reading API keys:', error);
      return {};
    }
  }

  /**
   * Delete API key
   * @param {string} provider - Provider name
   */
  static deleteApiKey(provider) {
    const keys = this.getAllApiKeys();
    delete keys[provider];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(keys));
  }

  /**
   * Check if API key exists for provider
   * @param {string} provider - Provider name
   * @returns {boolean}
   */
  static hasApiKey(provider) {
    return !!this.getApiKey(provider);
  }

  /**
   * Clear all API keys
   */
  static clearAllApiKeys() {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Get API keys for specific tools
   * @param {Array} tools - Array of tool configurations
   * @returns {Object} Map of provider -> API key
   */
  static getApiKeysForTools(tools) {
    const apiKeys = {};
    
    tools.forEach(tool => {
      if (tool.requiresApiKey?.required) {
        const provider = tool.requiresApiKey.provider;
        const key = this.getApiKey(provider);
        if (key) {
          apiKeys[provider] = key;
        }
      }
    });
    
    return apiKeys;
  }

  /**
   * Get list of providers with stored API keys
   * @returns {Array} Array of provider names
   */
  static getStoredProviders() {
    const keys = this.getAllApiKeys();
    return Object.keys(keys);
  }

  /**
   * Validate API key format (basic validation)
   * @param {string} provider - Provider name
   * @param {string} apiKey - API key to validate
   * @returns {boolean} True if format is valid
   */
  static validateApiKeyFormat(provider, apiKey) {
    if (!apiKey || typeof apiKey !== 'string') {
      return false;
    }

    // Basic validation rules
    switch (provider) {
      case 'brave':
        // Brave API keys are typically alphanumeric
        return apiKey.length > 10 && /^[A-Za-z0-9_-]+$/.test(apiKey);
      
      case 'openai':
        // OpenAI keys start with 'sk-'
        return apiKey.startsWith('sk-') && apiKey.length > 20;
      
      case 'deepseek':
        // DeepSeek keys start with 'sk-'
        return apiKey.startsWith('sk-') && apiKey.length > 20;
      
      default:
        // Generic validation: at least 10 characters
        return apiKey.length >= 10;
    }
  }
}

